import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, Camera, User, Lock, Mail, Phone, Shield, Eye, EyeOff } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MonComptePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const avatarRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: '',
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        setForm({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
        });
      } catch { toast.error('Erreur chargement profil'); }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/update-profile`, form);
      setUser(res.data);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, first_name: res.data.first_name, last_name: res.data.last_name, email: res.data.email, phone: res.data.phone }));
      toast.success('Profil mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Mot de passe modifié');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de changement de mot de passe');
    }
    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data.url || res.data.profile_image;
      setUser(prev => ({ ...prev, profile_image: newUrl }));
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, profile_image: newUrl }));
      toast.success('Photo de profil mise à jour');
    } catch { toast.error('Erreur upload photo'); }
    setUploadingAvatar(false);
  };

  const roleLabels = {
    super_admin: 'Super Administrateur',
    regional: 'Responsable Régional',
    tresorier: 'Trésorier',
    auteur: 'Auteur',
    ophthalmologist: 'Ophtalmologiste',
    intern: 'Interne',
  };

  const avatarSrc = user?.profile_image
    ? (user.profile_image.startsWith('http') ? user.profile_image : `${BACKEND_URL}${user.profile_image}`)
    : null;

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-3xl mx-auto" data-testid="mon-compte-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {/* Avatar */}
          <div className="relative group cursor-pointer" onClick={() => avatarRef.current?.click()} data-testid="avatar-upload">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-100 bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
              {uploadingAvatar ? (
                <Loader2 size={24} className="animate-spin text-white" />
              ) : avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xl font-bold">{(user?.first_name?.[0] || '')}{(user?.last_name?.[0] || '')}</span>
              )}
            </div>
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={18} className="text-white" />
            </div>
            <input ref={avatarRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {user?.first_name} {user?.last_name}
            </h1>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
              {roleLabels[user?.role] || user?.role}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
          {[
            { id: 'profile', label: 'Informations personnelles', icon: User },
            { id: 'security', label: 'Sécurité', icon: Lock },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${t.id}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Prénom</label>
                <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="input-first-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Nom</label>
                <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="input-last-name" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="input-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Téléphone</label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
                  placeholder="06 00 00 00 00" data-testid="input-phone" />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={handleSaveProfile} disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#1e3a5f] text-white rounded-xl text-sm font-bold hover:bg-[#162d4a] disabled:opacity-50 transition-colors"
                data-testid="save-profile-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
              </button>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Mot de passe actuel</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} value={passwordForm.current_password}
                  onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                  className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="current-password" />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Nouveau mot de passe</label>
                <input type={showPassword ? 'text' : 'password'} value={passwordForm.new_password}
                  onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="new-password" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Confirmer</label>
                <input type={showPassword ? 'text' : 'password'} value={passwordForm.confirm_password}
                  onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid="confirm-password" />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={handleChangePassword} disabled={saving || !passwordForm.current_password || !passwordForm.new_password}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#1e3a5f] text-white rounded-xl text-sm font-bold hover:bg-[#162d4a] disabled:opacity-50 transition-colors"
                data-testid="change-password-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />} Modifier le mot de passe
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MonComptePage;
